import { useCallback, useEffect, useMemo, useState } from 'react';
import { NamiCustomerManager } from '@namiml/web-sdk';
import EditableButton from '../components/EditableButton';
import ErrorMessage from '../components/ErrorMessage';

interface AttributeRow {
  key: string;
  value: string;
}

const parseAttributes = (entries: string[]): AttributeRow[] =>
  entries
    .map((entry) => {
      const [rawKey, ...rest] = entry.split(':');
      const rawValue = rest.join(':');
      return {
        key: rawKey?.trim() ?? '',
        value: rawValue?.trim() ?? '',
      };
    })
    .filter((row) => row.key.length > 0);

const SettingsPage = () => {
  const [keyDraft, setKeyDraft] = useState('');
  const [valueDraft, setValueDraft] = useState('');
  const [attributes, setAttributes] = useState<AttributeRow[]>([]);
  const [inAnonymousMode, setInAnonymousMode] = useState(false);
  const [error, setError] = useState('');

  const loadAttributes = useCallback(() => {
    try {
      const rows = parseAttributes(NamiCustomerManager.getAllCustomerAttributes());
      setAttributes(rows);
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to load attributes: ${details}`);
    }
  }, []);

  const loadAnonymousMode = useCallback(() => {
    try {
      setInAnonymousMode(NamiCustomerManager.inAnonymousMode());
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to read anonymous mode: ${details}`);
    }
  }, []);

  useEffect(() => {
    loadAttributes();
    loadAnonymousMode();
  }, [loadAnonymousMode, loadAttributes]);

  const handleSetAttribute = useCallback(() => {
    try {
      if (!keyDraft.trim() || !valueDraft.trim()) {
        setError('Key and value are required.');
        return;
      }
      NamiCustomerManager.setCustomerAttribute(keyDraft.trim(), valueDraft.trim());
      setKeyDraft('');
      setValueDraft('');
      loadAttributes();
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to set attribute: ${details}`);
    }
  }, [keyDraft, valueDraft, loadAttributes]);

  const handleClearAttribute = useCallback(
    (key: string) => {
      try {
        NamiCustomerManager.clearCustomerAttribute(key);
        loadAttributes();
      } catch (err) {
        const details = err instanceof Error ? err.message : String(err);
        setError(`Failed to clear attribute: ${details}`);
      }
    },
    [loadAttributes]
  );

  const handleClearAllAttributes = useCallback(() => {
    try {
      NamiCustomerManager.clearAllCustomerAttributes();
      loadAttributes();
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to clear attributes: ${details}`);
    }
  }, [loadAttributes]);

  const toggleAnonymousMode = useCallback(() => {
    try {
      NamiCustomerManager.setAnonymousMode(!inAnonymousMode);
      setInAnonymousMode(!inAnonymousMode);
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to toggle anonymous mode: ${details}`);
    }
  }, [inAnonymousMode]);

  const anonymousModeLabel = useMemo(
    () => (inAnonymousMode ? 'Turn Anonymous Mode Off' : 'Turn Anonymous Mode On'),
    [inAnonymousMode]
  );

  return (
    <div className="screen-root">
      <div className="panel stack">
        <h1 className="panel-title">Customer Settings</h1>
        <p className="panel-subtitle">Manage attributes and anonymous mode.</p>
        {error && <ErrorMessage message={error} />}

        <div className="panel">
          <h2 className="panel-title">Set Customer Attribute</h2>
          <div className="option-grid">
            <EditableButton
              label="Attribute Key"
              value={keyDraft}
              placeholder="Enter key"
              onSubmit={setKeyDraft}
            />
            <EditableButton
              label="Attribute Value"
              value={valueDraft}
              placeholder="Enter value"
              onSubmit={setValueDraft}
            />
            <button type="button" className="tv-button" onClick={handleSetAttribute}>
              Send
            </button>
          </div>
        </div>

        <div className="panel">
          <h2 className="panel-title">Stored Attributes</h2>
          <div className="stack">
            {attributes.length === 0 && (
              <div className="panel-subtitle">No custom attributes stored.</div>
            )}
            {attributes.map((row) => (
              <div key={row.key} className="list-row">
                <span>{row.key}</span>
                <span>{row.value}</span>
                <button
                  type="button"
                  className="tv-button"
                  data-variant="ghost"
                  onClick={() => handleClearAttribute(row.key)}
                >
                  Clear
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="inline-actions" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="tv-button" onClick={handleClearAllAttributes}>
            Clear All Attributes
          </button>
          <button type="button" className="tv-button" onClick={toggleAnonymousMode}>
            {anonymousModeLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
